import React, { useContext } from 'react';
import { ICategoryContext, CategoryContext } from '../../../../categoryState/categoryContext';
import { categoryTypes, typesOfCategoriesDeniedForClientManagers } from 'appConstants';
import { UserRoles, ICategoryV2 } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import { useLoggedUserRestaurants } from 'hooks/useLoggedUserRestaurants';
import Text from 'components/Text/Text';
import { ImgUploaderByPreSignedUrl } from 'components/ImgUploader/ImgUploaderByPreSignedUrl';
import { RestaurantsAsLinkList } from 'components/Lists/ListView/RestaurantsAsLinkList';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IProps {
	data: ICategoryV2['items'][0];
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion, setIDForAssignRestaurants } = useContext<ICategoryContext>(CategoryContext);
	const isDeniedForManagers = typesOfCategoriesDeniedForClientManagers.includes(data.type);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		isDeniedForManagers ? UserRoles.NON_EXISTENT_ROLE : UserRoles.CLIENT_MANAGER,
		isDeniedForManagers ? UserRoles.NON_EXISTENT_ROLE : UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
		UserRoles.WAITER,
		UserRoles.RKL_SUPPORT_MANAGER,
	]);
	const categoryRowClasses = classNames(style.category, style.row);
	const categoryNameClasses = classNames(style.categoryName);
	const categoryTypeRowClasses = classNames(style.categoryType, style.row);
	const numberOfRestaurantsRowClasses = classNames(style.restaurant, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const categoryTypeName = categoryTypes.find((el) => el.categoryID === data.type)?.categoryName || null;
	const restaurants = data.restaurants.map((restaurant) => ({ id: restaurant.id, restaurantName: restaurant.name }));
	const accessibleRestaurants = useLoggedUserRestaurants(restaurants);

	const editButtonElement = accessibleRestaurants.length ? (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/category/edit?categoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsList" size="s" />
			</div>
			<Link to={`/menu-v2/category/edit-restaurant-properties?categoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
		</div>
	) : (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/category/edit?categoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="assignRestaurants" size="s" />
			</div>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<div className={categoryRowClasses}>
					<div className={style.imgWrapper}>
						<ImgUploaderByPreSignedUrl loaderSize="small" preSignedUrl={data.preSignedUrl} />
					</div>
					<Text
						className={categoryNameClasses}
						translationKey={data.name}
						withTooltip={true}
						data-test="categoryName"
					/>
				</div>
				<Text
					className={categoryTypeRowClasses}
					translationNameSpace="form"
					translationKey={categoryTypeName}
					withTooltip={true}
					data-test="categoryType"
				/>
				<div className={numberOfRestaurantsRowClasses}>
					<RestaurantsAsLinkList restaurants={accessibleRestaurants} />
				</div>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/category/view?categoryID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
